/**
 * Touch-DnD polyfill — synthesizes HTML5 drag-and-drop events from
 * touch events so existing `onDragStart` / `onDragOver` / `onDrop`
 * handlers work on mobile / tablet.
 *
 * Behaviour:
 *   1. On `touchstart` over a `[draggable=true]` element, arm a
 *      pendingStart record (single primary touch only).
 *   2. On `touchmove`, if past long-press threshold (250 ms) OR
 *      move-threshold (8 px), promote to active drag by dispatching
 *      a synthetic `dragstart` and capturing a floating clone of the
 *      source as drag preview.
 *   3. Walk under the finger via `elementFromPoint`, dispatching
 *      `dragenter` / `dragover` / `dragleave` as the target changes.
 *      Move the floating clone with the finger.
 *   4. On `touchend`, dispatch `drop` on the final target, then
 *      `dragend` on the source.  Remove the preview clone.
 *   5. `touchcancel` cleans up without firing drop.
 *
 * Multi-touch handling: we identify our captured touch by
 * `touchIdentifier`.  Additional concurrent touches are ignored —
 * they don't interrupt the active drag or hijack the pendingStart.
 *
 * Install once at app mount via installTouchDnDBridge().
 */

type ActiveDrag = {
  sourceEl: HTMLElement;
  data: Map<string, string>;
  lastTarget: HTMLElement | null;
  effectAllowed: string;
  touchId: number;
  preview: HTMLElement | null;
};

type PendingStart = {
  touchId: number;
  x: number;
  y: number;
  el: HTMLElement;
  t: number;
};

let active: ActiveDrag | null = null;
let pendingStart: PendingStart | null = null;
let installed = false;
/** (#9 round-6) Track listener registrations so __TOUCH_DND_TEST_ONLY__.reset
 *  can fully uninstall them between tests.  In prod we never uninstall,
 *  but tests want clean slate. */
let registeredListeners: Array<[string, EventListener, AddEventListenerOptions | boolean]> = [];

// (#9 round-7) Magic numbers live in shared/config/tuning.ts.
import { TOUCH_DND } from '@/shared/config/tuning';
const LONG_PRESS_MS = TOUCH_DND.longPressMs;
const MOVE_THRESHOLD_PX = TOUCH_DND.scrollCancelPx;
// Marker we attach to synthetic events so coexisting native DnD (on
// hybrid mouse+touch devices) doesn't double-handle.
const SYNTH_FLAG = '__holzcomTouchSynth';

function makeDataTransferShim(data: Map<string, string>, effectAllowed: string): DataTransfer {
  const dataTransferShim = {
    get types() { return Array.from(data.keys()); },
    getData: (mime: string) => data.get(mime) ?? '',
    setData: (mime: string, val: string) => { data.set(mime, val); },
    clearData: (mime?: string) => {
      if (mime) data.delete(mime);
      else data.clear();
    },
    dropEffect: 'move' as DataTransfer['dropEffect'],
    effectAllowed: effectAllowed as DataTransfer['effectAllowed'],
    files: [] as unknown as FileList,
    items: [] as unknown as DataTransferItemList,
    setDragImage: () => {},
  };
  return dataTransferShim as unknown as DataTransfer;
}

function findDraggable(t: EventTarget | null): HTMLElement | null {
  // Guard: TouchEvent.target may be Document or Window when the touch
  // started outside the document body — only walk HTMLElement nodes.
  if (!t || !(t instanceof Element)) return null;
  let el: HTMLElement | null = t as HTMLElement;
  while (el && el !== document.body) {
    if (el.draggable === true || el.getAttribute?.('draggable') === 'true') return el;
    el = el.parentElement;
  }
  return null;
}

function dispatch(target: HTMLElement, type: string, dt: DataTransfer, clientX: number, clientY: number): Event {
  const ev = new Event(type, { bubbles: true, cancelable: true });
  Object.defineProperty(ev, 'dataTransfer', { value: dt, configurable: true });
  Object.defineProperty(ev, 'clientX', { value: clientX, configurable: true });
  Object.defineProperty(ev, 'clientY', { value: clientY, configurable: true });
  // (#1) Tag synthesized events so native DnD listeners on
  // mouse+touch hybrids can opt out via this marker.
  (ev as unknown as Record<string, boolean>)[SYNTH_FLAG] = true;
  target.dispatchEvent(ev);
  return ev;
}

/** True when this event was synthesized by our touch polyfill.  Use
 *  in native onDragStart/onDrop handlers to avoid double-handling on
 *  hybrid touch+mouse devices. */
export function isSyntheticTouchDrag(ev: Event): boolean {
  return Boolean((ev as unknown as Record<string, boolean>)[SYNTH_FLAG]);
}

/** True while the touch polyfill is actively driving a synthesized drag.
 *  Native pointer-drag paths (e.g. the fill-op pointer ghost) consult this to
 *  avoid double-handling the same gesture on touch+mouse hybrid devices. */
export function isTouchDragActive(): boolean {
  return active !== null;
}

function isTouch(): boolean {
  return typeof window !== 'undefined' && 'ontouchstart' in window;
}

/** (#9) Build a floating clone of the source element to follow the
 *  finger as a drag preview.  Clone is appended to body, fixed-positioned,
 *  semi-transparent, pointer-events:none. */
function makePreview(source: HTMLElement, clientX: number, clientY: number): HTMLElement {
  const clone = source.cloneNode(true) as HTMLElement;
  // (#15+#13) Strip id="" attributes from the clone subtree so a
  // floating preview doesn't collide with the live document's IDs.
  // We deliberately KEEP name="" — form inputs (radio groups, etc.)
  // need it to remain semantically intact; React may also rely on it.
  clone.removeAttribute('id');
  clone.querySelectorAll('[id]').forEach((el) => el.removeAttribute('id'));
  // (#9 round-5) Strip CSS transitions/animations from EVERY node in
  // the clone subtree so the preview stays anchored to the finger
  // (no inherited hover/focus easing animating it out from under us).
  // (#12 round-7) Use setProperty(..., 'important') to override stylesheet
  // rules that may carry !important.  Inline assignment alone can lose
  // the cascade fight.
  const killMotion = (el: HTMLElement) => {
    el.style.setProperty('transition', 'none', 'important');
    el.style.setProperty('animation', 'none', 'important');
  };
  killMotion(clone);
  clone.querySelectorAll('*').forEach((el) => killMotion(el as HTMLElement));
  const rect = source.getBoundingClientRect();
  clone.style.position = 'fixed';
  clone.style.top = '0';
  clone.style.left = '0';
  clone.style.width = `${rect.width}px`;
  clone.style.height = `${rect.height}px`;
  clone.style.transform = `translate(${clientX - rect.width / 2}px, ${clientY - rect.height / 2}px)`;
  clone.style.opacity = String(TOUCH_DND.previewOpacity);
  clone.style.pointerEvents = 'none';
  clone.style.zIndex = '99999';
  clone.style.boxShadow = '0 4px 12px rgba(0,0,0,0.25)';
  clone.style.borderRadius = '4px';
  clone.style.transition = 'none';
  document.body.appendChild(clone);
  return clone;
}

function movePreview(preview: HTMLElement, clientX: number, clientY: number): void {
  const w = preview.offsetWidth;
  const h = preview.offsetHeight;
  preview.style.transform = `translate(${clientX - w / 2}px, ${clientY - h / 2}px)`;
}

function findTouch(list: TouchList, id: number): Touch | null {
  for (let i = 0; i < list.length; i++) {
    if (list[i].identifier === id) return list[i];
  }
  return null;
}

function cleanup(): void {
  if (active?.preview && active.preview.parentNode) {
    active.preview.parentNode.removeChild(active.preview);
  }
  active = null;
  pendingStart = null;
}

function on(type: string, handler: EventListener, opts: AddEventListenerOptions | boolean = false) {
  document.addEventListener(type, handler, opts);
  registeredListeners.push([type, handler, opts]);
}

// (#11 round-8) Track the pending long-press timer alongside listeners
// so uninstall() guarantees no callback fires after we've torn down.
let trackedPendingTimer: number | null = null;
function setPendingTimer(t: number | null): void {
  trackedPendingTimer = t;
}

function uninstall(): void {
  for (const [type, handler, opts] of registeredListeners) {
    document.removeEventListener(type, handler, opts);
  }
  registeredListeners = [];
  if (trackedPendingTimer !== null) {
    window.clearTimeout(trackedPendingTimer);
    trackedPendingTimer = null;
  }
}

export function installTouchDnDBridge(): void {
  if (!isTouch()) return;
  // (#5 round-7) Double-install safety: if someone calls install twice
  // (HMR, re-import, test races) we uninstall the previous batch
  // first so registeredListeners doesn't lose track of the old refs.
  if (installed) uninstall();
  installed = true;

  on('touchstart', ((ev: TouchEvent) => {
    // (#8) Ignore additional touches once a drag is in progress.
    if (active || pendingStart) return;
    // Only use the primary touch.
    if (ev.touches.length !== 1) return;
    const el = findDraggable(ev.target);
    if (!el) return;
    const touch = ev.touches[0];
    pendingStart = {
      touchId: touch.identifier,
      x: touch.clientX, y: touch.clientY,
      el, t: performance.now(),
    };
    armLongPress();
  }) as EventListener, { passive: true });

  // (#2) Schedule a long-press promotion on touchstart instead of
  // promoting "as soon as user moves 8 px".  This way a vertical
  // swipe (scroll intent) within the first LONG_PRESS_MS doesn't
  // accidentally start a drag — it just cancels pendingStart.
  let pendingTimer: number | null = null;
  function armLongPress() {
    if (pendingTimer !== null) window.clearTimeout(pendingTimer);
    pendingTimer = window.setTimeout(() => {
      pendingTimer = null;
      setPendingTimer(null);
      if (!pendingStart || active) return;
      const data = new Map<string, string>();
      const dt = makeDataTransferShim(data, 'all');
      dispatch(pendingStart.el, 'dragstart', dt, pendingStart.x, pendingStart.y);
      if (data.size === 0) { pendingStart = null; return; }
      active = {
        sourceEl: pendingStart.el, data, lastTarget: null,
        effectAllowed: 'all',
        touchId: pendingStart.touchId,
        preview: makePreview(pendingStart.el, pendingStart.x, pendingStart.y),
      };
      pendingStart = null;
    }, LONG_PRESS_MS);
    setPendingTimer(pendingTimer);
  }

  on('touchmove', ((ev: TouchEvent) => {
    // (#2) During long-press wait: if finger moved >MOVE_THRESHOLD_PX
    // before timer fires, treat as a scroll — cancel pendingStart.
    if (pendingStart && !active) {
      const touch = findTouch(ev.touches, pendingStart.touchId);
      if (!touch) return;
      const dx = touch.clientX - pendingStart.x;
      const dy = touch.clientY - pendingStart.y;
      if (dx * dx + dy * dy >= MOVE_THRESHOLD_PX * MOVE_THRESHOLD_PX) {
        if (pendingTimer !== null) { window.clearTimeout(pendingTimer); pendingTimer = null; }
        pendingStart = null;
      }
    }

    if (!active) return;
    // Only track our captured touch — additional fingers are ignored.
    const touch = findTouch(ev.touches, active.touchId);
    if (!touch) return;

    movePreview(active.preview!, touch.clientX, touch.clientY);

    // Hide preview during elementFromPoint so we hit-test the row below.
    const previewDisplay = active.preview!.style.display;
    active.preview!.style.display = 'none';
    const under = document.elementFromPoint(touch.clientX, touch.clientY) as HTMLElement | null;
    active.preview!.style.display = previewDisplay;

    const dt = makeDataTransferShim(active.data, active.effectAllowed);
    if (active.lastTarget && active.lastTarget !== under) {
      dispatch(active.lastTarget, 'dragleave', dt, touch.clientX, touch.clientY);
    }
    if (under) {
      if (active.lastTarget !== under) {
        dispatch(under, 'dragenter', dt, touch.clientX, touch.clientY);
      }
      dispatch(under, 'dragover', dt, touch.clientX, touch.clientY);
    }
    active.lastTarget = under;
    ev.preventDefault();
  }) as EventListener, { passive: false });

  const finish = (ev: TouchEvent) => {
    // Always cancel pending long-press timer on any release.
    if (pendingTimer !== null) { window.clearTimeout(pendingTimer); pendingTimer = null; }
    // (#8) Clear pendingStart whenever its touch lifts — regardless of
    // whether it matched or not.  Old behaviour left pendingStart
    // armed when a second finger lifted before promotion.
    if (pendingStart) {
      // Find ANY changedTouch matching the pending touchId.
      const matched = findTouch(ev.changedTouches, pendingStart.touchId);
      if (matched) pendingStart = null;
      // If our pending touch isn't in changedTouches, AND no other
      // touches remain on screen, the gesture is over.  Be conservative
      // and drop the pending state.
      else if (ev.touches.length === 0) pendingStart = null;
      return;
    }
    if (!active) return;
    const touch = findTouch(ev.changedTouches, active.touchId);
    if (!touch) return;  // not our touch
    const dt = makeDataTransferShim(active.data, active.effectAllowed);
    if (active.lastTarget) {
      dispatch(active.lastTarget, 'drop', dt, touch.clientX, touch.clientY);
    }
    dispatch(active.sourceEl, 'dragend', dt, touch.clientX, touch.clientY);
    cleanup();
  };
  on('touchend', finish as EventListener);
  // (#7) On touchcancel — still fire dragend on the source so any
  // user UI cleanup (highlight removal, etc.) runs.  No drop event.
  on('touchcancel', ((ev: TouchEvent) => {
    if (pendingTimer !== null) { window.clearTimeout(pendingTimer); pendingTimer = null; }
    if (pendingStart) {
      const matched = findTouch(ev.changedTouches, pendingStart.touchId);
      if (matched || ev.touches.length === 0) pendingStart = null;
      return;
    }
    if (!active) return;
    const touch = findTouch(ev.changedTouches, active.touchId)
              ?? ev.changedTouches[0]
              ?? { clientX: 0, clientY: 0 } as Touch;
    const dt = makeDataTransferShim(active.data, active.effectAllowed);
    if (active.lastTarget) {
      dispatch(active.lastTarget, 'dragleave', dt, touch.clientX, touch.clientY);
    }
    dispatch(active.sourceEl, 'dragend', dt, touch.clientX, touch.clientY);
    cleanup();
  }) as EventListener);
}

// Test hooks — export the inner state shape so vitest can reset.
export const __TOUCH_DND_TEST_ONLY__ = {
  reset: () => { cleanup(); uninstall(); installed = false; },
  install: installTouchDnDBridge,
};
